import { useEffect, useRef } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TagWithName } from '@/models/Tag';
import { BaseInputProps } from '@/components/ui/custom/dataInput/BaseInput';
import { useTags } from './TagInput.hooks';
import { TagPopup } from './TagPopup';
import TagsManager from './TagsManager';

type TagField = {
  tags: TagWithName[];
};

type Props = BaseInputProps & {
  value?: Array<number>;
  onChange: (value: Array<number>) => void;
};

export default function TagInput(props: Props) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'lessonForm',
  });

  const { onChange, value = [], className } = props;

  const { control } = useForm<TagField>();

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'tags',
    keyName: 'tempId',
  });

  const { tags, loading } = useTags({
    value,
  });

  const initialized = useRef(false);

  useEffect(() => {
    if (loading) return;

    if (tags && tags.length > 0 && !initialized.current) {
      replace(tags);
      initialized.current = true;
    }
  }, [tags, replace, loading]);

  function onAdd(tag: TagWithName) {
    append(tag);
    onChange([...fields.map(value => value.id), tag.id]);
  }

  function onDelete(tagId: number) {
    const index = value.findIndex(value => value === tagId);

    remove(index);

    const filterNewValues = fields.filter(value => value.id !== tagId);

    const newValues = filterNewValues.map(value => value.id);

    onChange(newValues);
  }

  return (
    <TagPopup
      label={t('topics')}
      tags={fields}
      onDelete={onDelete}
      loading={loading}
      className={{
        ...className,
        li: 'bg-secondary rounded-full px-1.5',
        text: 'text-base-100',
        button: 'text-base-100',
      }}
    >
      <TagsManager currentTags={fields} onAdd={onAdd} onDelete={onDelete} />
    </TagPopup>
  );
}
